import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import React, { useEffect, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link, useNavigate } from 'react-router-dom';
import {
  CONTACT_METHOD_TYPE,
  CONTACT_METHOD_VERIFICATION_TYPE,
} from 'vendor-marketplace-shared-library';

import useAlert from '../../hooks/useAlert';
import useAuth from '../../hooks/useAuth';
import UserService from '../../services/user.service';
import theme from '../../utilities/theme';
import ContactMethodVerification from './components/ContactMethodVerification';
import { FAILED_TO_CREATE_AUTH_CHALLENGE } from '../../error-messages';

export default function LoginPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { setAlert, clearAlert } = useAlert();

  const [loading, setLoading] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [contactMethodType, setContactMethodType] = useState<string>(CONTACT_METHOD_TYPE.PHONE);
  const [contactMethodVerificationId, setContactMethodVerificationId] = useState<number>();
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (auth.accessToken) navigate('/');
  }, [auth.accessToken, navigate]);

  const createAuthChallenge = async () => {
    clearAlert();

    try {
      setLoading(true);
      const response = await UserService.createAuthChallenge(
        CONTACT_METHOD_VERIFICATION_TYPE.SIGNIN,
        contactMethodType,
        contactMethodType === CONTACT_METHOD_TYPE.PHONE ? phone : email,
      );
      if (response.data.newUser) {
        setOpen(true);
      }
      setContactMethodVerificationId(response.data.contactMethodVerificationId);
    } catch (error: any) {
      setAlert({
        message: error.response?.data?.error || FAILED_TO_CREATE_AUTH_CHALLENGE,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container sx={styles.mainContainer}>
      <Grid item>
        <Grid container sx={styles.subContainer}>
          <Grid item>
            <img src={'procure-logo-black.svg'} alt={'procure'} height={100} />
          </Grid>

          {!contactMethodVerificationId ? (
            <Grid item>
              <Grid container sx={styles.inputContainer}>
                <Grid item sx={styles.fullWidth}>
                  <ToggleButtonGroup
                    color='primary'
                    value={contactMethodType}
                    exclusive
                    onChange={(event: React.MouseEvent<HTMLElement>, contactMethodType: string) => {
                      if (!contactMethodType) return;
                      setContactMethodType(contactMethodType);
                    }}
                  >
                    <ToggleButton value={CONTACT_METHOD_TYPE.PHONE}>Phone</ToggleButton>
                    <ToggleButton value={CONTACT_METHOD_TYPE.EMAIL}>Email</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item sx={styles.fullWidth}>
                  {contactMethodType === CONTACT_METHOD_TYPE.PHONE ? (
                    <Grid container sx={styles.input}>
                      <Grid item>
                        <Typography>Enter your phone number to get started</Typography>
                      </Grid>
                      <Grid item sx={styles.fullWidth}>
                        <MuiTelInput
                          fullWidth
                          value={phone}
                          onChange={setPhone}
                          placeholder='Phone Number'
                          defaultCountry='US'
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container sx={styles.input}>
                      <Grid item>
                        <Typography>Enter your email to get started</Typography>
                      </Grid>
                      <Grid item sx={styles.fullWidth}>
                        <TextField
                          fullWidth
                          value={email}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setEmail(event.target.value)
                          }
                          placeholder='Email'
                          type='email'
                          required
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>

                <Grid item sx={styles.fullWidth}>
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <Button
                      sx={styles.fullWidth}
                      variant={'outlined'}
                      onClick={createAuthChallenge}
                    >
                      Get Started
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid item>
              <ContactMethodVerification
                contactMethodVerificationId={contactMethodVerificationId}
                contactMethodVerificationType={CONTACT_METHOD_VERIFICATION_TYPE.SIGNIN}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item px={5}>
        <Typography variant={'caption'} color={theme.palette.secondary.main}>
          {"By continuing, you agree to ProCure's"}{' '}
          <Link to={'https://www.procurefix.com/privacy-policy'}>Privacy Policy</Link>{' '}
          {'and agree to receive text messages. Carrier fees may apply.'}
        </Typography>
      </Grid>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Box sx={styles.modal}>
          <Grid container sx={styles.modalContainer}>
            <Grid item>
              <Typography sx={styles.modalText}>
                {`This ${contactMethodType} is not associated with an account. Would you like to create a
                new account?`}
              </Typography>
            </Grid>
            <Grid item>
              <Grid container sx={styles.gap}>
                <Grid item>
                  <Button onClick={() => setOpen(false)} variant='contained'>
                    Create Account
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      auth.logout();
                      setOpen(false);
                      setContactMethodVerificationId(undefined);
                      setPhone('');
                      setEmail('');
                    }}
                  >
                    Exit
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Grid>
  );
}

const styles = {
  mainContainer: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '90vh',
    paddingTop: 10,
    paddingBottom: 4,
    textAlign: 'center',
  },
  subContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: 8,
  },
  inputContainer: {
    minWidth: 300,
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: 2,
  },
  input: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: 2,
  },
  fullWidth: {
    width: 1,
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    padding: 4,
    borderRadius: 2,
  },
  modalContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: 2,
  },
  modalText: {
    mt: 2,
    textAlign: 'center',
  },
  gap: {
    columnGap: 2,
  },
};
